/**
 * **打印 IR（RenderTree）编译器** —— 把「页面 + 组件」编译成**绘制指令表**。
 *
 * ```text
 *   节点图求值 ──► 页面值 / 组件值（mm）      ← evaluators.ts
 *        │
 *        │  compilePage()   ← 本文件：**唯一一次 mm → 设备点**
 *        ▼
 *   PageRenderTree { widthDots, heightDots, ops: DrawOp[] }   ← 可序列化、可 diff、
 *        │                                                        可快照测试
 *        ▼
 *   输出适配器（ZPL / TSPL / ESC-POS / 1bpp 光栅）—— **C++ 侧**（架构文档 D7）
 * ```
 *
 * ## 这份 IR 是**和执行侧的契约**，所以三条纪律很硬
 *
 * ① **单位是设备点，而且是整数**（架构文档 §4.3 / §4.4）。
 *    mm 只活在文档层；这里已经是"打印机数得出来的格子"。
 *    换算走 `units.ts` 那**一个** `mmToDots` —— 多一处换算，就多一处
 *    「预览 799 点、打印 800 点」的机会。
 *
 * ② **每个 op 的坐标都是绝对页面坐标**，没有隐含偏移。
 *    条码的静区**已经算进坐标里**（`xDots` 是含静区的占地左边缘，
 *    条本身在 `bars` 里、也是绝对值）—— 让执行侧自己加偏移，
 *    迟早有一端忘了加，而"条码贴边"这种错只有扫码枪看得出。
 *
 * ③ **编译期就把问题说清楚**（`problems`）：越界 / 越过边距 / 模块太窄 /
 *    编码失败。它们是**生产端拒绝执行的依据**（架构文档 §2.8：
 *    生产端没有人看着，跳过问题继续打出来是一张缺东西的单）。
 *
 * ⚠️ 和 `graph/serialization.ts` 的 `toRenderTree()` **不是一回事**：
 * 那个是**节点图的功能数据**（谁连谁、参数是什么，给求值用）；
 * 这里的是**绘制指令**（画到第几个点、多宽）。两处同名是历史原因，
 * 但它们的消费者、单位、生命周期都不同 —— 文件名因此叫 `renderTree.ts`
 * 而类型都带 `Print` 前缀。
 */
import { lineHeightOf, type BarcodeView, type ElementView, type PageView } from './pageModel'
import { normalizeJob, type PrintJob } from './job'
import { textMeasurerFor, type TextMeasurer } from './measure'
import { DEFAULT_DPI, mmToDots } from './units'

/* ══════════════════════════════════════════════════════════════════
   绘制指令
   ══════════════════════════════════════════════════════════════════ */

/** 文本对齐（和组件上的枚举同词，别另起一套） */
export type TextAlign = 'left' | 'center' | 'right'

/**
 * 一条绘制指令。**几何全是整数设备点**。
 *
 * ⚠️ 只列**今天真的会产出**的三种 op（文本 / 矩形 / 条码）。
 * 架构文档 §4.3 里还有 `line` / `image` / `glyphs` / `device`，
 * 那些等有对应组件（线条 / 图片 / 字体子集 / 设备动作节点）时再加 ——
 * 先声明一堆没人产出的 op，执行侧就得为它们写一堆没人走过的分支。
 */
/**
 * **旋转**：绕 `pivotXDots` / `pivotYDots` 顺时针 `rotationDeg` 度。
 *
 * ⚠️ 枢轴必须**显式写出来**，而且一个组件的所有 op 共用**同一个**枢轴
 * （组件框的中心）—— 条码的 HRI 是一条独立的 text op，它自己那行的中心
 * 和条码框的中心不是一回事：只给角度不给枢轴的话，旋转后的条码
 * 会出现"条转了、人眼可读那行留在原地"。
 */
export interface PrintRotation {
  rotationDeg: number
  pivotXDots: number
  pivotYDots: number
}

export type PrintDrawOp =
  | {
      op: 'text'
      /** 文本框左边缘（对齐在渲染侧按 `align` 处理，和文档层的语义一致） */
      xDots: number
      /** 文本**基线**上方那一行的顶边（渲染侧按字号推基线） */
      yDots: number
      sizeDots: number
      align: TextAlign
      text: string
      /** 加粗（执行侧用字体资源，见 `fontId` 那条警告） */
      bold: boolean
      /** 行距（点）：多行的行间基线距离（IR 里已经是**算好的**值，不是"0 = 自动"） */
      lineHeightDots: number
      /**
       * **这一行画出来占多宽**（点）—— **测量层的产物**（架构 §4.2 的 ①）。
       *
       * ⚠️ 它冻在这里的意义：执行侧**不要再量一次**。各端测量实现不同
       * （浏览器 Canvas2D 与 Skia 差零点几像素），而居中对齐要用到宽度 ——
       * 重量的结果就是"编辑器里居中、打出来偏左"。
       *
       * ⚠️ 只有**单行**（`text` 里的换行符按最长那一行算）——
       * 换行 / 断词还没做（见 `measure.ts` 的边界说明）。
       */
      advanceDots: number
      /** 基线到行顶（点）—— 同样是冻进来的，执行侧不猜 */
      ascentDots: number
      /**
       * **文本框宽度**（点）—— 对齐要用的那个"框"。
       *
       * ⚠️ 有它之后，`center` / `right` 的落点可以**只用冻好的数字**算出来：
       * `x + (boxWDots - advanceDots) / 2`。执行侧于是完全不需要测量，
       * 也不需要知道"这个框多宽"（那是文档层的事）。
       */
      boxWDots: number
      /** 这份数字是谁测的（`canvas2d` / `fallback`）—— 估出来的值要能一路看见 */
      measuredBy: string
      /** 垂直对齐：框比文字高时贴在框的哪一边 */
      vAlign: 'top' | 'middle' | 'bottom'
      /** 反白：先铺实心黑再掏空字（热敏打不出白墨，见 `type-contract` §8.1） */
      invert: boolean
      /** 文字颜色（`#rrggbb`）—— 单色机上"非白即墨"，多色机按它分版 */
      color: string
      /**
       * 不透明度（0–1）。
       *
       * ⚠️ 热敏是**二值**的：半透明只能靠**抖动**近似，而抖不抖由执行侧决定 ——
       * 不支持的机型**忽略它**（当作 1），别报错也别当成"不用打印"。
       */
      opacity: number
      rotation?: PrintRotation
      /** 来自哪个组件（预览按它分组做圆角裁剪；排错时也一眼知道是谁画的） */
      elementId?: string
      /** 圆角裁剪（只有 `radiusMm > 0` 才写 —— 见 `PrintClip`） */
      clip?: PrintClip
      /**
       * 字体标识。
       *
       * ⚠️ 今天恒为 `'default'`：**字体资源还没进文档模型**
       * （架构文档 §10.1 的 `fonts` 那一节是后话）。写死一个 id 而不是
       * 留空，是为了让执行侧现在就有一个字段可读 —— 但别把它当成
       * "字体已经定了"（HRI 尤其：§5.5 明确说**不能用打印机内置字体**）。
       */
      fontId: string
    }
  | {
      op: 'rect'
      xDots: number
      yDots: number
      wDots: number
      hDots: number
      /** 线宽（点）；0 = 只填充不描边 */
      lineWidthDots: number
      fill: boolean
      /** 边框 / 填充颜色（`#rrggbb`） */
      strokeColor: string
      fillColor: string
      /** 圆角半径（点）；0 = 直角 */
      radiusDots: number
      invert: boolean
      /** 不透明度（0–1）—— 见 `text` 那条同样的说明 */
      opacity: number
      rotation?: PrintRotation
      /** 来自哪个组件（预览按它分组做圆角裁剪；排错时也一眼知道是谁画的） */
      elementId?: string
      /** 圆角裁剪（只有 `radiusMm > 0` 才写 —— 见 `PrintClip`） */
      clip?: PrintClip
    }
  | {
      op: 'image'
      xDots: number
      yDots: number
      wDots: number
      hDots: number
      /**
       * 图片地址（URL / `data:` URI）。
       *
       * ⚠️ 资产库还没做，所以这里可能是**外部地址** —— 执行侧要能接受
       * "取不到图"（IR 里同时给了一条 warning），而不要把整页拒掉。
       */
      src: string
      fit: 'contain' | 'cover' | 'fill'
      /** 二值化阈值 0–255（热敏只有上墨 / 不上墨） */
      threshold: number
      rotation?: PrintRotation
      /** 来自哪个组件（预览按它分组做圆角裁剪；排错时也一眼知道是谁画的） */
      elementId?: string
      /** 圆角裁剪（只有 `radiusMm > 0` 才写 —— 见 `PrintClip`） */
      clip?: PrintClip
    }
  | {
      op: 'barcode'
      kind: '1d'
      /** **占地**（含静区）左边缘 */
      xDots: number
      yDots: number
      heightDots: number
      /** 一个模块几个点（整数 —— 架构文档 §5.1 的硬约束） */
      moduleDots: number
      /** 两侧静区（点）—— 已经在 `xDots` / `bars` 里算过了，这里只是给人看/校验用 */
      quietZoneDots: number
      /** 条：**绝对坐标**（不是相对内容区） */
      bars: { xDots: number; wDots: number }[]
      /**
       * **前景色**（`#rrggbb`）—— 条的颜色，也就是"墨"。
       *
       * ⚠️ 单色机上只有"上墨 / 不上墨"，所以颜色只对**多色带**机器有意义
       * （黑 + 红那种）。默认黑。
       */
      color: string
      rotation?: PrintRotation
      /** 来自哪个组件（预览按它分组做圆角裁剪；排错时也一眼知道是谁画的） */
      elementId?: string
      /** 圆角裁剪（只有 `radiusMm > 0` 才写 —— 见 `PrintClip`） */
      clip?: PrintClip
    }
  | {
      op: 'barcode'
      kind: '2d'
      /** 矩阵（**内容区**，不含静区）左上角 */
      xDots: number
      yDots: number
      moduleDots: number
      quietZoneDots: number
      cols: number
      rows: number
      /**
       * 模块矩阵：**每行一个 `'0'/'1'` 字符串**（行序从上到下）。
       *
       * ⚠️ 用字符串而不是 `boolean[]`：一份 IR 要能存盘、能 diff、能进快照测试，
       * 而"第 37 位从 1 变 0"在字符串里一眼看得见（数组里要数数）。
       * 体积也更小（`JSON.stringify` 见方就长）。
       */
      matrix: string[]
      /** **前景色**（模块的颜色）—— 见 1d 那条同样的说明 */
      color: string
      rotation?: PrintRotation
      /** 来自哪个组件（预览按它分组做圆角裁剪；排错时也一眼知道是谁画的） */
      elementId?: string
      /** 圆角裁剪（只有 `radiusMm > 0` 才写 —— 见 `PrintClip`） */
      clip?: PrintClip
    }

/** 编译期发现的一个问题 */
export interface PrintIrProblem {
  severity: 'error' | 'warning'
  /** 出问题的组件（页面级问题没有这一项） */
  elementId?: string
  message: string
}

/** 一个页面的绘制指令表 */
/**
 * **圆角裁剪**：这一条 op 只允许画在这个圆角矩形里。
 *
 * ⚠️ 为什么要有它：编辑器里圆角是**真的在裁**（`overflow: hidden`），
 * 而 IR 原来只是"把框画圆"—— 里面的条码 / 图片照样溢到圆角外面去。
 * 编辑器与执行侧不一致比缺功能更坏：用户会相信一件不成立的事。
 *
 * ⚠️ 只有 `radiusMm > 0` 才写（绝大多数组件没有圆角，IR 不该为它变胖）。
 * 执行侧拿到它就按"圆角矩形蒙版"处理（1-bit 输出 = 圆角外的点不上墨）。
 */
export interface PrintClip {
  xDots: number
  yDots: number
  wDots: number
  hDots: number
  radiusDots: number
}

export interface PageRenderTree {
  pageId: string
  /** 页面尺寸（点）—— 这就是"打印机要打多大" */
  widthDots: number
  heightDots: number
  dpi: number
  /** 边距（点）：内容不该越过的边界 */
  marginDots: number
  /*
   * ⚠️ **这里刻意没有"纸色"**（`background`）。
   *
   * 纸色是**预览属性**：它模拟的是"这张标签贴的介质长什么样"
   * （白纸 / 黑纸 / 彩色纸），**不改变打印内容** —— 打印机只知道
   * "哪里上墨"，不知道纸是什么颜色。
   *
   * 第一版把它放进了 IR（还写着"白 = 不铺底、别的颜色 = 整张铺满"），
   * 那是**把预览当成了指令**：执行侧照着铺一整张黑，标签就废了。
   * 所以它在文档里存着（编辑器与预览读它），但**不进 IR**。
   * `verify:print` 有一条断言专门钉住"IR 里没有它"。
   */
  ops: PrintDrawOp[]
  problems: PrintIrProblem[]
}

/** **交给执行侧的那份文件**（`design_lib` 读它） */
export interface PrintRenderTreeFile {
  format: 'node-print.rendertree'
  version: 1
  /**
   * **这一份活怎么打**（份数 / 切刀 / 浓度 / 速度 / 介质 / 走纸）。
   *
   * ⚠️ 它在**文件级**（不是每页一份）：一台机器一次只可能用一套介质与浓度。
   * 页与页之间可变的是"内容"，不是"机器怎么走纸"。
   *
   * ⚠️ 按 **D7**，这里只有**机型无关**的语义；翻译成 ZPL `^MD` / TSPL `DENSITY`
   * 那一步归执行侧（C++）—— 各机型语法不同，天然不需要两端一致。
   */
  job: PrintJob
  pages: PageRenderTree[]
}

export const RENDER_TREE_FORMAT = 'node-print.rendertree'
export const RENDER_TREE_VERSION = 1

/* ══════════════════════════════════════════════════════════════════
   编译
   ══════════════════════════════════════════════════════════════════ */

/** 文本小到这个点数就没法读了（203dpi 下 8 点 = 1mm）—— 编译期提醒一句 */
const MIN_TEXT_DOTS = 8

/**
 * **编译一个页面**（架构文档 §3 的 `CanvasLayout` 策略：绝对定位 + 旋转）。
 *
 * ⚠️ 顺序 = **数组顺序**（= `items.N` 的序号 = 层序）：后画的压在上面。
 * IR 是**有序**指令表，不是集合 —— 执行侧照着顺序画就行，
 * 不需要理解"谁在上面"。
 */
export function compilePage(
  /**
   * ⚠️ **测量器从外面传**（默认按 dpi 取宿主那一份）。
   * 纯逻辑验证脚本（esbuild + node，没有画布）显式传 `fallbackTextMeasurer` ——
   * 这样 IR 里那些字宽数字是"谁测的"可指定、可断言（`measuredBy`）。
   */
  page: PageView,
  measure?: TextMeasurer,
): PageRenderTree {
  const measurer = measure ?? textMeasurerFor(page.dpi || DEFAULT_DPI)
  const dpi = page.dpi || DEFAULT_DPI
  const widthDots = mmToDots(page.widthMm, dpi)
  const heightDots = mmToDots(page.heightMm, dpi)
  const marginDots = mmToDots(page.marginMm, dpi)
  const ops: PrintDrawOp[] = []
  const problems: PrintIrProblem[] = []

  for (const element of page.elements) {
    compileElement(element, dpi, ops, problems, measurer)

    /* ── 越界 / 压边距：拿**框**（mm）判，它是所有组件都有的共同分母 ── */
    const box = element.box
    const left = mmToDots(box.xMm, dpi)
    const top = mmToDots(box.yMm, dpi)
    const right = mmToDots(box.xMm + box.wMm, dpi)
    const bottom = mmToDots(box.yMm + box.hMm, dpi)
    if (left < 0 || top < 0 || right > widthDots || bottom > heightDots) {
      problems.push({
        severity: 'error',
        elementId: element.id,
        message: `「${element.title}」超出纸面（${left},${top} → ${right},${bottom} 点，纸是 ${widthDots}×${heightDots}）—— 超出去的部分打不出来`,
      })
    } else if (
      left < marginDots ||
      top < marginDots ||
      right > widthDots - marginDots ||
      bottom > heightDots - marginDots
    ) {
      problems.push({
        severity: 'warning',
        elementId: element.id,
        message: `「${element.title}」压到了边距（纸边 ${page.marginMm}mm 内）—— 打印机在那里通常打不出东西`,
      })
    }
  }

  return { pageId: page.id, widthDots, heightDots, dpi, marginDots, ops, problems }
}

/**
 * 一个组件的**旋转枢轴**（点）—— 永远是**组件框的中心**。
 *
 * ⚠️ 组件的所有 op（条码的条、它的 HRI）都拿这一个枢轴：HRI 是独立的一条
 * text op，如果执行侧按"那条 text 自己的中心"去转，旋转 90° 之后
 * 条和人眼可读那行会**分家**（一个转到了右边、一个还留在下边）。
 */
function pivotOf(element: ElementView, dpi: number): PrintRotation | undefined {
  if (element.rotationDeg % 360 === 0) return undefined
  return {
    rotationDeg: element.rotationDeg,
    pivotXDots: mmToDots(element.box.xMm + element.box.wMm / 2, dpi),
    pivotYDots: mmToDots(element.box.yMm + element.box.hMm / 2, dpi),
  }
}

/** 这一条 op 的公共装饰：来自谁 + 要不要圆角裁剪 */
function decorate(element: ElementView, dpi: number): { elementId: string; clip?: PrintClip } {
  const base = { elementId: element.id }
  if (element.radiusMm <= 0) return base
  return {
    ...base,
    clip: {
      xDots: mmToDots(element.box.xMm, dpi),
      yDots: mmToDots(element.box.yMm, dpi),
      wDots: mmToDots(element.box.wMm, dpi),
      hDots: mmToDots(element.box.hMm, dpi),
      radiusDots: mmToDots(element.radiusMm, dpi),
    },
  }
}

/**
 * **组件的框**：有边框或有底色时，先画一条 `rect` op。
 *
 * ⚠️ 这条不是"顺手加的"：编辑器里每个组件都画边框 / 底色（那是矩形的遗产），
 * 而 IR 里原来**只有** `rect` 类型的组件带这些 —— 于是"给条码加个白底、
 * 给二维码加个圆角框"在编辑器里看得见、**打印出来没有**。
 * 编辑器与 IR 不一致比缺功能更坏：它让人相信一件不成立的事。
 *
 * ⚠️ 文本 / 图片 / 条码 / 二维码都走它（`invert` 的实心底由各自的 op 负责，
 * 这里只管**可见的框与底**）。
 *
 * ⚠️★ **转过的组件：框要带上同一个枢轴**（`pivotOf`）。编辑器里框层长在
 * `.npp__ink` 里 —— 那一层才是 `rotate(...)` 的作用对象，所以框跟着转；
 * 这一条原来**没有** `rotation`，于是预览 / 打印那边会画出一个**没转的框**，
 * 内容却在里面转（症状是"框和内容是歪的"，只有出纸才看得见）。
 * 框的**几何**仍然是没转的组件框（旋转只作用在绘制上：命中、吸附、裁剪
 * 都不受影响）—— 这正是 IR 里 `rotation` 与 `xDots/yDots` 的分工。
 */
function pushFrameOp(element: ElementView, dpi: number, ops: PrintDrawOp[]): void {
  const hasBorder = element.lineWidthMm > 0
  if (!hasBorder && !element.fill) return
  const rotation = pivotOf(element, dpi)
  ops.push({
    op: 'rect',
    xDots: mmToDots(element.box.xMm, dpi),
    yDots: mmToDots(element.box.yMm, dpi),
    wDots: mmToDots(element.box.wMm, dpi),
    hDots: mmToDots(element.box.hMm, dpi),
    lineWidthDots: hasBorder ? Math.max(1, mmToDots(element.lineWidthMm, dpi)) : 0,
    fill: element.fill,
    opacity: element.opacity,
    strokeColor: element.strokeColor,
    fillColor: element.fillColor,
    radiusDots: mmToDots(element.radiusMm, dpi),
    invert: false,
    ...(rotation ? { rotation } : {}),
    ...decorate(element, dpi),
  })
}

function compileElement(
  element: ElementView,
  dpi: number,
  ops: PrintDrawOp[],
  problems: PrintIrProblem[],
  measure: TextMeasurer,
): void {
  /*
   * ⚠️ 框先画（底在内容之下）—— 除了 `rect` 自己：它的 op 本来就是框。
   *
   * ⚠️ 条码也**不在这里**画框：它那条路上"编码失败 → 什么都不产"，
   * 框放进 `compileBarcode` 里（见那边的说明），否则会留下一个空框。
   */
  if (element.kind !== 'box' && element.kind !== 'barcode') pushFrameOp(element, dpi, ops)
  if (element.kind === 'barcode') {
    compileBarcode(element, dpi, ops, problems, measure)
    return
  }
  const rotation = pivotOf(element, dpi)
  if (element.kind === 'image') {
    const view = element.image
    if (view && !view.hasSource) {
      problems.push({
        severity: 'warning',
        elementId: element.id,
        message: `「${element.title}」还没有图像源（填地址或 data: URI）—— 这一块会空着`,
      })
    }
    ops.push({
      op: 'image',
      xDots: mmToDots(element.box.xMm, dpi),
      yDots: mmToDots(element.box.yMm, dpi),
      wDots: mmToDots(element.box.wMm, dpi),
      hDots: mmToDots(element.box.hMm, dpi),
      src: view?.src ?? '',
      fit: view?.fit ?? 'contain',
      threshold: view?.threshold ?? 128,
      ...(rotation ? { rotation } : {}),
      ...decorate(element, dpi),
    })
    return
  }
  if (element.kind === 'box') {
    ops.push({
      op: 'rect',
      xDots: mmToDots(element.box.xMm, dpi),
      yDots: mmToDots(element.box.yMm, dpi),
      wDots: mmToDots(element.box.wMm, dpi),
      hDots: mmToDots(element.box.hMm, dpi),
      lineWidthDots: mmToDots(element.lineWidthMm, dpi),
      fill: element.fill,
      opacity: element.opacity,
      strokeColor: element.strokeColor,
      fillColor: element.fillColor,
      radiusDots: mmToDots(element.radiusMm, dpi),
      invert: element.invert,
      ...(rotation ? { rotation } : {}),
      ...decorate(element, dpi),
    })
    return
  }

  /* ── 文本 ── */
  const sizeDots = Math.max(1, mmToDots(element.sizeMm, dpi))
  if (sizeDots < MIN_TEXT_DOTS) {
    problems.push({
      severity: 'warning',
      elementId: element.id,
      message: `「${element.title}」的字号只有 ${sizeDots} 个点（${dpi}dpi 下不到 1mm）—— 打出来基本读不了`,
    })
  }
  /*
   * ★ **量一次，冻进来**（架构 §4.2 / §6.2）：执行侧拿到 `advanceDots` 就直接摆，
   * 不再自己测量 —— 两端测量实现不同，重量的结果就是"居中在浏览器里对、
   * 打出来偏一点"。
   */
  const measured = measure.measure({
    text: element.text,
    sizeDots,
    lineHeightDots: Math.max(1, mmToDots(lineHeightOf(element), dpi)),
    fontId: 'default',
  })
  ops.push({
    op: 'text',
    xDots: mmToDots(element.box.xMm, dpi),
    yDots: mmToDots(element.box.yMm, dpi),
    sizeDots,
    align: element.align,
    text: element.text,
    fontId: 'default',
    /* ↓ 测量层的产物（谁测的也写上：估出来的值要能一路看见） */
    advanceDots: measured.advanceDots,
    ascentDots: measured.ascentDots,
    boxWDots: mmToDots(element.box.wMm, dpi),
    measuredBy: measure.id,
    bold: element.bold,
    /*
     * ⚠️ 行距在 IR 里是**算好的值**（不是"0 = 自动"）：执行侧不该知道
     * "1.15 倍"这条规矩 —— 那是文档层的排版策略，两头各算一次就会
     * "编辑器里 3 行、打印出来 2 行半"。
     */
    lineHeightDots: Math.max(1, mmToDots(lineHeightOf(element), dpi)),
    vAlign: element.vAlign,
    invert: element.invert,
    color: element.color,
    opacity: element.opacity,
    ...(rotation ? { rotation } : {}),
    ...decorate(element, dpi),
  })
}

function compileBarcode(
  element: ElementView,
  dpi: number,
  ops: PrintDrawOp[],
  problems: PrintIrProblem[],
  measure: TextMeasurer,
): void {
  const view = element.barcode
  if (!view) return

  /* 编码失败：IR 里**不能有一条画得出来的假条码** —— 报 error，什么都不产 */
  if (!view.encoded) {
    problems.push({
      severity: 'error',
      elementId: element.id,
      message: view.error ?? `「${element.title}」没能编码`,
    })
    return
  }
  if (view.xDimWarning) {
    problems.push({ severity: 'warning', elementId: element.id, message: view.xDimWarning })
  }
  /* ★ 框太粗 / 目标宽度太窄（内容放不下）—— `barcodeElementOf` 算出来的那句话 */
  if (view.frameWarning) {
    problems.push({ severity: 'warning', elementId: element.id, message: view.frameWarning })
  }

  /*
   * ★ **框 / 底在编码成功之后才画**（`pushFrameOp`）。
   *
   * ⚠️ 顺序不能挪到 `compileElement` 里去（那里是"先画框再分派"的统一写法）：
   * 编码失败时那条 `return` 会让"什么都不产"变成"产一个空框"——
   * 一张只有边框、里面什么都没有的标签比没有更坏（而且它还会被当成正常指令发出去）。
   * 所以条码这一条路上，框归这里画，且画在内容**之前**（IR 的指令顺序 = 层序）。
   */
  pushFrameOp(element, dpi, ops)

  /*
   * ⚠️★ **内容要躲开框**：`insetDots` 是框让出来的那一圈（**半个线宽**，
   * 见 `pageModel.barcodeElementOf` 的 `insetMm`）。静区的外边缘正好贴着框的
   * 内边缘 —— 框画在纸上，静区在框里面，两者**不重叠**。
   * 不内缩的话，描边的内侧半个线宽会压在静区里（扫码率的前提）。
   */
  const insetDots = view.insetMm > 0 ? Math.max(0, mmToDots(view.insetMm, dpi)) : 0
  /* 占地（含静区）的左 / 上边缘 = 组件框 + 内缩 */
  const originX = mmToDots(element.box.xMm, dpi) + insetDots
  const originY = mmToDots(element.box.yMm, dpi) + insetDots
  const moduleDots = view.moduleDots
  /* ⚠️ 换算只走 `mmToDots` —— 这里手写 `mm / (25.4/dpi)` 就等于第二处换算 */
  const quietZoneDots = mmToDots(view.quietZoneMm, dpi)
  const rotation = pivotOf(element, dpi)

  if (view.encoded.kind === '1d') {
    const heightDots = mmToDots(view.barHeightMm, dpi)
    ops.push({
      op: 'barcode',
      kind: '1d',
      xDots: originX,
      yDots: originY,
      heightDots,
      moduleDots,
      quietZoneDots,
      /* ⚠️ 绝对值：内容从静区之后开始 —— 执行侧不需要知道静区的存在 */
      bars: view.encoded.bars.map((bar) => ({
        xDots: originX + quietZoneDots + bar.x * moduleDots,
        wDots: bar.w * moduleDots,
      })),
      /* 「颜色」= 条的颜色（单色机上非白即墨，多色带才真的分版） */
      color: view.color,
      ...(rotation ? { rotation } : {}),
      ...decorate(element, dpi),
    })
    /* HRI 是**另一条 text op**（架构文档 §5.5：和正文同一条绘制路径） */
    if (view.showHri && view.hriMm > 0) {
      const hriSizeDots = Math.max(1, mmToDots(view.hriMm, dpi))
      const hriMeasured = measure.measure({
        text: view.text,
        sizeDots: hriSizeDots,
        lineHeightDots: hriSizeDots,
        fontId: 'default',
      })
      ops.push({
        op: 'text',
        xDots: originX,
        yDots: originY + heightDots,
        sizeDots: hriSizeDots,
        align: 'center',
        text: view.text,
        fontId: 'default',
        bold: false,
        /* HRI 单行：行距给字号本身（多行时才是行间距离） */
        lineHeightDots: hriSizeDots,
        advanceDots: hriMeasured.advanceDots,
        ascentDots: hriMeasured.ascentDots,
        /*
         * HRI 的对齐框 = 条码**内容**的占地宽度（它居中在条上）。
         * ⚠️ 内缩是两侧对称的，所以"居中"这件事不受影响；但框宽要按内容算 ——
         * 拿组件框宽（含两侧内缩）会让那行字在视觉上仍居中、执行侧按错框对齐
         * 时就偏了（`align` 是给执行侧算落点用的）。
         */
        boxWDots: mmToDots(element.box.wMm, dpi) - insetDots * 2,
        measuredBy: measure.id,
        vAlign: 'top',
        invert: false,
        color: element.color,
        opacity: element.opacity,
        /* ⚠️ 和条码**共用**枢轴：不共用的话转 90° 之后条和 HRI 会分家 */
        ...(rotation ? { rotation } : {}),
        /*
         * ⚠️★ `elementId` / `clip` 也要带上（`decorate` 就是这两样）。
         *
         * 契约里写着"**每条 op 都带 `elementId`**"（`type-contract` §8.1），
         * 而这一条原来漏了 —— 漏了不报错，只是那行字在 IR 里**认不出是谁画的**
         * （排错时最要紧的那个字段），而且它和条码**本该是同一条 `clip`**：
         * 同一个组件、同一个圆角，一个被裁一个不裁，两个视图就分家了。
         *
         * ⚠️ 说清楚现状：条码元素的 `radiusMm` 今天在 `barcodeElementOf` 里
         * 还写死着 0（外观那一组参数在条码上没接上，见那边的说明），
         * 所以这条 `clip` 眼下不会真的出现 —— 但"漏一条 op"这件事不该留着。
         */
        ...decorate(element, dpi),
      })
    }
    return
  }

  /* ── 2D：矩阵原点在静区之后 ── */
  const matrix: string[] = []
  for (let row = 0; row < view.encoded.rows; row += 1) {
    let line = ''
    for (let col = 0; col < view.encoded.cols; col += 1) {
      line += view.encoded.matrix[row * view.encoded.cols + col] ? '1' : '0'
    }
    matrix.push(line)
  }
  ops.push({
    op: 'barcode',
    kind: '2d',
    xDots: originX + quietZoneDots,
    yDots: originY + quietZoneDots,
    moduleDots,
    quietZoneDots,
    cols: view.encoded.cols,
    rows: view.encoded.rows,
    matrix,
    color: view.color,
    ...(rotation ? { rotation } : {}),
    ...decorate(element, dpi),
  })
}

/* ══════════════════════════════════════════════════════════════════
   交付件
   ══════════════════════════════════════════════════════════════════ */

/**
 * 把若干页面编译成**交给执行侧的那份文件**。
 *
 * ⚠️ 它是**纯数据**（`JSON.stringify` 得出来的就是执行侧的输入）：
 * 不含函数、不含 DOM、不含 Vue 的响应式包装。
 * `verify:print` 有一条断言钉着"往返之后逐字节相同"——
 * 哪天有人在 op 里塞了个 `Map` 或 `undefined`，那条会红。
 */
export function compileToFile(
  pages: readonly PageView[],
  job?: Partial<PrintJob>,
  /** 测量器（同 `compilePage`：纯逻辑脚本显式传降级实现） */
  measure?: TextMeasurer,
): PrintRenderTreeFile {
  return {
    format: RENDER_TREE_FORMAT,
    version: RENDER_TREE_VERSION,
    /* ⚠️ 过一遍 `normalizeJob`：手改/导入的文档里那些值不可信 */
    job: normalizeJob(job),
    pages: pages.map((page) => compilePage(page, measure)),
  }
}

/** 这份产物里有多少个 error / warning（宿主用它决定要不要拦） */
export function problemCounts(file: PrintRenderTreeFile): {
  errors: number
  warnings: number
} {
  let errors = 0
  let warnings = 0
  for (const page of file.pages) {
    for (const problem of page.problems) {
      if (problem.severity === 'error') errors += 1
      else warnings += 1
    }
  }
  return { errors, warnings }
}

/**
 * **「1 个点 = 1 个 CSS px」的缩放。**
 *
 * 编辑视图按物理尺寸画（1mm = 3.78px），而 203dpi 的一个点只有 0.125mm
 * —— 也就是**半个屏幕像素**：在那个尺度上看不见"条宽是 3 个点还是 4 个点"，
 * 而这类整数吸附正是打印域最容易出错的地方。
 * 所以精确预览要有一档"把点放大到看得见"的缩放。
 */
export function dotZoom(dpi: number): number {
  const effective = dpi || DEFAULT_DPI
  /* px/点 = (96/25.4)·zoom·(25.4/dpi) = 96·zoom/dpi，让它 = 1 → zoom = dpi/96 */
  return effective / 96
}
